import { nanoid } from "nanoid";

/**
 * Local persistence for this TV's identity + pairing binding. Stored in
 * localStorage - wiped by `?reset=1` URL flag or by an admin "Unpair" from
 * the CMS (which we detect via lack of the screen on the server).
 *
 * The `screenToken` is the server-issued credential that authenticates
 * every API call this TV makes. Without it, the TV gets 401 from /api/.
 */

const SCREEN_ID_KEY = "foyer.screenId";
const BINDING_KEY = "foyer.binding";

/**
 * Device IDENTITY keys - everything else under the `foyer.` namespace is guest
 * state. Exported so the checkout wipe can clear guest state without unpairing
 * the TV (see lib/checkoutWipe.ts).
 */
export const BINDING_KEYS = [SCREEN_ID_KEY, BINDING_KEY] as const;

/**
 * Durable storage. On a native shell (Android kiosk) this is SharedPreferences,
 * which survives a reboot and a WebView-storage wipe - neither of which plain
 * localStorage guarantees on hotel-TV firmware (the "TV asks to pair again after
 * reboot" bug). On web/preview the native store is absent and we use localStorage.
 *
 * Reads prefer the native store; if it's empty but localStorage has a value
 * (a TV paired on an older build), we migrate it into native on read. Writes go
 * to BOTH so the durable copy always exists and localStorage stays a usable
 * mirror.
 */
function nativeStore() {
  const n = typeof window !== "undefined" ? window.FoyerNative : undefined;
  if (!n || typeof n.storeGet !== "function") return null;
  return {
    get(k: string): string | null {
      try {
        return n.storeGet!(k) ?? null;
      } catch {
        return null;
      }
    },
    set(k: string, v: string) {
      try {
        n.storeSet?.(k, v);
      } catch {
        // ignore
      }
    },
    remove(k: string) {
      try {
        n.storeRemove?.(k);
      } catch {
        // ignore
      }
    },
  };
}

export function durableGet(key: string): string | null {
  const ns = nativeStore();
  if (ns) {
    const native = ns.get(key);
    if (native != null) return native;
    // Native empty - migrate an existing localStorage value (older install).
    try {
      const ls = localStorage.getItem(key);
      if (ls != null) {
        ns.set(key, ls);
        return ls;
      }
    } catch {
      // ignore
    }
    return null;
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function durableSet(key: string, value: string): void {
  nativeStore()?.set(key, value);
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

/**
 * Durable read that does NOT migrate a localStorage value into the native
 * store, unlike {@link durableGet}.
 *
 * For values written by {@link durableSetBounded}, where "localStorage has it
 * and the native store deliberately does not" is a legitimate state. The
 * migrate-on-read in `durableGet` would silently undo that decision on the very
 * next read.
 */
export function durableGetRaw(key: string): string | null {
  const native = nativeStore()?.get(key);
  if (native != null) return native;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Durable write with a ceiling on what goes into the native store.
 *
 * The native store is Android SharedPreferences - an XML file held in memory
 * and rewritten wholesale on every commit. It is exactly right for a pairing
 * binding (a few hundred bytes, written once) and a poor place for an
 * unbounded blob. Anything over `maxNativeBytes` stays in localStorage only,
 * and any previous native copy is REMOVED rather than left behind: a stale
 * durable copy that outranks the fresh localStorage one on read is worse than
 * no durable copy at all.
 */
export function durableSetBounded(
  key: string,
  value: string,
  maxNativeBytes: number
): void {
  const ns = nativeStore();
  if (ns) {
    if (value.length <= maxNativeBytes) ns.set(key, value);
    else ns.remove(key);
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

export function durableRemove(key: string): void {
  nativeStore()?.remove(key);
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export interface Binding {
  premiseId: string;
  roomId: string;
  /** Server-issued opaque token. Sent on every API call. */
  screenToken: string;
}

/** Returns this TV's stable id, creating one on first run. */
export function getScreenId(): string {
  const cached = durableGet(SCREEN_ID_KEY);
  if (cached) return cached;
  const id = `scr-${nanoid(10)}`;
  durableSet(SCREEN_ID_KEY, id);
  return id;
}

/**
 * Adopt a specific screen id (durably). Used when the TV re-attaches by serial
 * after a storage wipe: the server returns the screen's ORIGINAL id, and we
 * adopt it so heartbeats keep reporting under the same device record instead of
 * a freshly-generated id.
 */
export function setScreenId(id: string): void {
  durableSet(SCREEN_ID_KEY, id);
}

export function getBinding(): Binding | null {
  try {
    const raw = durableGet(BINDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Binding;
    // Drop pre-token-era bindings - they're missing the credential we
    // now require, so the TV must re-pair.
    if (parsed.premiseId && parsed.roomId && parsed.screenToken) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * The screen token from a binding that is too damaged to use - the durable
 * record survived but lost `premiseId`/`roomId` (a partial wipe, an interrupted
 * write). `getBinding()` correctly refuses to return such a record, but the
 * token itself is still this device's strongest proof of identity, so the
 * serial re-attach presents it as its second factor rather than falling back to
 * the weaker same-network check. Returns null when there's nothing to salvage.
 */
export function getSalvagedToken(): string | null {
  try {
    const raw = durableGet(BINDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Binding>;
    return typeof parsed.screenToken === "string" && parsed.screenToken
      ? parsed.screenToken
      : null;
  } catch {
    return null;
  }
}

export function setBinding(binding: Binding): void {
  durableSet(BINDING_KEY, JSON.stringify(binding));
}

export function clearBinding(): void {
  durableRemove(BINDING_KEY);
}

/** Best-effort guess of the TV model - used at pairing for fleet view. */
export function guessModel(): string {
  if (typeof navigator === "undefined") return "Unknown TV";
  const ua = navigator.userAgent;
  // Philips NetTV (older MediaSuite firmware): "NETTV/4.5.0" → "Philips NetTV 4.5.0".
  const nettv = ua.match(/NETTV\/([\d.]+)/i);
  if (nettv) return `Philips MediaSuite (NetTV ${nettv[1]})`;
  // Android WebView (incl. Android-based MediaSuite): the model token sits
  // after the Android version. Most UAs end it with " Build/", but some omit
  // that suffix, so we also accept a closing ")" or ";" as the terminator.
  const android = ua.match(/Android\s+[\d.]+;\s*([^;)]+?)(?:\s+Build\/|[);])/i);
  if (android) {
    const model = android[1].trim();
    // Ignore generic AOSP placeholders that aren't a real model name.
    if (model && !/^(aosp|android|generic)$/i.test(model)) return model;
  }
  // Many MediaSuite firmwares use a Vewd / CogniTV / CE-HTML browser whose UA
  // has no NETTV token (e.g. "Mozilla/5.0 (Linux armv7l) ... CE-HTML/1.0 ..."
  // or the Vewd/Opera-TV "OPR/.. SmartTvA/.." signature on Philips Pro TVs).
  // Catch any remaining Philips/TV signal so we don't fall through to
  // navigator.platform, which is "ARM7" / "Linux armv7l" on these devices.
  if (/philips|mediasuite|cogni|ce-html|\bvewd\b|smarttva|\bavm\b/i.test(ua)) {
    return "Philips MediaSuite";
  }
  // Final fallback: navigator.platform. On desktops this is meaningful
  // ("MacIntel", "Win32"); on embedded TV firmwares it's a bare CPU
  // architecture ("ARM7", "armv7l", "Linux armv7l", "aarch64") that is
  // useless as a "model" - surfacing it is the "ARM7 in the device list"
  // bug. Only pass platform through when it looks like a real desktop/mobile
  // platform; otherwise the device is an unidentified TV.
  const platform = (navigator.platform || "").trim();
  if (!platform) return "Browser preview";
  const isArchOnly =
    /\b(arm\w*|aarch64|mips\w*|sh4|ppc\w*)\b/i.test(platform) &&
    !/(x86|x64|win|mac|intel|iphone|ipad)/i.test(platform);
  return isArchOnly ? "Unknown TV" : platform;
}

/**
 * Is there ANY stored binding record, even one `getBinding()` refuses to
 * return? Diagnostics only: separates "the store was wiped" from "the store
 * kept something we rejected as incomplete".
 */
export function hasRawBinding(): boolean {
  return durableGet(BINDING_KEY) != null;
}
